import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import {
  User, CreditCard, Crown, Calendar, Shield, LogOut,
  MessageSquare, ExternalLink, CheckCircle2, AlertCircle, Sparkles, Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import FeedbackDialog from "../components/FeedbackDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_COLORS = {
  free:     { bg: "from-slate-600/20 to-slate-900/5",   text: "text-slate-300",   ring: "ring-slate-500/30" },
  starter:  { bg: "from-sky-600/20 to-sky-900/5",       text: "text-sky-300",     ring: "ring-sky-500/30" },
  pro:      { bg: "from-violet-600/20 to-violet-900/5", text: "text-violet-300",  ring: "ring-violet-500/30" },
  business: { bg: "from-amber-600/20 to-amber-900/5",   text: "text-amber-300",   ring: "ring-amber-500/30" },
  enterprise:{bg: "from-emerald-600/20 to-emerald-900/5",text:"text-emerald-300", ring: "ring-emerald-500/30" },
};

const STATUS_LABEL = {
  active: { label: "Aktiv", icon: CheckCircle2, color: "text-emerald-400" },
  trialing: { label: "Testphase", icon: Sparkles, color: "text-amber-400" },
  past_due: { label: "Zahlung ausstehend", icon: AlertCircle, color: "text-orange-400" },
  canceled: { label: "Gekündigt", icon: AlertCircle, color: "text-rose-400" },
  expired: { label: "Abgelaufen", icon: AlertCircle, color: "text-rose-400" },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch (_) { return "—"; }
};

export const Konto = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(searchParams.get("tab") === "feedback");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/subscriptions/current`, { withCredentials: true });
        setSubscription(r.data);
      } catch (_) {
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sub = subscription?.subscription;
  const planConfig = subscription?.plan_config || {};
  const planId = sub?.plan || "free";
  const planColors = PLAN_COLORS[planId] || PLAN_COLORS.free;
  const statusCfg = STATUS_LABEL[sub?.status] || STATUS_LABEL.trialing;
  const StatusIcon = statusCfg.icon;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="konto-loading">
        <div className="text-slate-400">Laden...</div>
      </div>
    );
  }

  return (
    <div data-testid="konto-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Konto</h1>
          <p className="page-subtitle">Profil, Abonnement & Zahlungen</p>
        </div>
      </header>

      <div className="px-6 md:px-10 py-6 space-y-6 max-w-5xl">
        {/* Profil */}
        <section
          className="rounded-2xl border border-slate-700/60 bg-slate-900/40 backdrop-blur-sm p-6"
          data-testid="konto-profile"
        >
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-500/30 to-indigo-600/20 ring-1 ring-inset ring-violet-500/30 flex items-center justify-center text-2xl font-semibold text-white">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-white truncate">{user?.name || "—"}</h2>
              <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-400">
                {user?.email && (
                  <span className="inline-flex items-center gap-1.5">
                    <Mail size={14} /> {user.email}
                  </span>
                )}
                {user?.role && (
                  <span className="inline-flex items-center gap-1.5">
                    <Shield size={14} /> {user.role === "admin" ? "Administrator" : "Benutzer"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Abonnement */}
        <section
          className={`rounded-2xl border border-slate-700/60 bg-gradient-to-br ${planColors.bg} bg-slate-900/40 backdrop-blur-sm p-6`}
          data-testid="konto-subscription"
        >
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl bg-slate-800/60 ring-1 ring-inset ${planColors.ring} ${planColors.text}`}>
                <Crown size={22} />
              </div>
              <div>
                <p className="text-[10.5px] uppercase tracking-wider text-slate-400">Dein Abonnement</p>
                <h3 className="text-xl font-semibold text-white capitalize">
                  {planConfig.display_name || planConfig.name || planId}
                </h3>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/70 text-xs font-medium ${statusCfg.color}`}>
              <StatusIcon size={12} />
              {statusCfg.label}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl bg-slate-800/40 border border-slate-700/50 p-4">
              <p className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1">Preis</p>
              <p className="text-lg font-semibold text-white">
                {planConfig.price ? `${planConfig.price.toFixed(2)} €` : "Kostenlos"}
                {planConfig.interval && planConfig.price ? (
                  <span className="text-sm font-normal text-slate-400"> / {planConfig.interval === "month" ? "Monat" : "Jahr"}</span>
                ) : null}
              </p>
            </div>
            <div className="rounded-xl bg-slate-800/40 border border-slate-700/50 p-4">
              <p className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1 inline-flex items-center gap-1.5">
                <Calendar size={11} /> Nächste Verlängerung
              </p>
              <p className="text-lg font-semibold text-white">
                {sub?.cancel_at_period_end ? (
                  <span className="text-rose-300">Endet am {formatDate(sub?.current_period_end)}</span>
                ) : (
                  formatDate(sub?.current_period_end)
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate("/pricing")}
              className="bg-violet-600 hover:bg-violet-500 text-white rounded-lg"
              data-testid="manage-subscription-btn"
            >
              <CreditCard size={16} className="mr-2" />
              Abonnement verwalten
            </Button>
            {planId === "free" && (
              <Button
                variant="outline"
                onClick={() => navigate("/pricing")}
                className="border-slate-600 text-white hover:bg-slate-800 rounded-lg"
                data-testid="upgrade-btn"
              >
                <Sparkles size={16} className="mr-2 text-amber-400" />
                Jetzt upgraden
              </Button>
            )}
          </div>
        </section>

        {/* Enthaltene Module */}
        {subscription?.accessible_modules?.length > 0 && (
          <section
            className="rounded-2xl border border-slate-700/60 bg-slate-900/40 backdrop-blur-sm p-6"
            data-testid="konto-modules"
          >
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-400" />
              In deinem Plan enthalten
            </h3>
            <div className="flex flex-wrap gap-2">
              {subscription.accessible_modules.map((mod) => (
                <span
                  key={mod}
                  className="text-xs px-3 py-1.5 rounded-full bg-slate-800/70 text-slate-300 border border-slate-700/50"
                >
                  {mod}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Aktionen */}
        <section
          className="rounded-2xl border border-slate-700/60 bg-slate-900/40 backdrop-blur-sm p-6"
          data-testid="konto-actions"
        >
          <h3 className="text-base font-semibold text-white mb-4">Weitere Aktionen</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              className="flex items-center gap-3 p-4 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-blue-500/40 transition-all text-left"
              data-testid="konto-feedback-btn"
            >
              <MessageSquare size={18} className="text-blue-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Feedback senden</p>
                <p className="text-xs text-slate-400">Vorschläge & Fehlerberichte</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/einstellungen")}
              className="flex items-center gap-3 p-4 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-violet-500/40 transition-all text-left"
              data-testid="konto-einstellungen-btn"
            >
              <User size={18} className="text-violet-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Unternehmensdaten</p>
                <p className="text-xs text-slate-400">Stammdaten bearbeiten</p>
              </div>
            </button>
            <a
              href="https://margevio.de"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 p-4 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-slate-500/40 transition-all"
              data-testid="konto-website-btn"
            >
              <ExternalLink size={18} className="text-slate-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Website & Support</p>
                <p className="text-xs text-slate-400">margevio.de</p>
              </div>
            </a>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-3 p-4 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-rose-500/40 transition-all text-left"
              data-testid="konto-logout-btn"
            >
              <LogOut size={18} className="text-rose-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Abmelden</p>
                <p className="text-xs text-slate-400">Aktuelle Sitzung beenden</p>
              </div>
            </button>
          </div>
        </section>
      </div>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
};

export default Konto;
